/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { PriceComponent } from '@msdyn365-commerce/components';
import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import { ChannelDeliveryOptionConfiguration, FilterDeliveryModeOption } from '@msdyn365-commerce/retail-proxy';
import { Address, Cart, CartLine, DeliveryOption } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import {
    GetDeliveryOptionsForCartLineAddressInput,
    getDeliveryOptionsForCartLineAddress, DeliveryOptionsCartLineAddress,
    ArrayExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, Waiting } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { Md5 } from 'ts-md5';
import get from 'lodash/get';
import stringify from 'json-stable-stringify';
import { action, computed, observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import groupBy from 'lodash/groupBy';

import { IDlvryCheckoutV2DeliveryOptionsData } from './dlvry-checkout-v2-delivery-options.data';
import {
    IDlvryCheckoutV2DeliveryOptionsProps,
    filterDeliveryModeOption,
    useDefaultDeliveryOption
} from './dlvry-checkout-v2-delivery-options.props.autogenerated';
import DescriptionComponent from './components/dlvry-checkout-v2-delivery-options.description';
import ErrorMessageComponent from './components/dlvry-checkout-v2-delivery-options.error-message';
import ErrorTitleComponent from './components/dlvry-checkout-v2-delivery-options.error-title';
import RadioButtonComponent from './components/dlvry-checkout-v2-delivery-options.radio-button';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/checkout';
import { AddressDetailItemFunctionComponent } from './components/address-detail-item';
import CheckoutCartLineDeliveryDate from './components/checkout-cartLine-delivery-date';
import CheckoutCartLineDeliveryOptionsRadio from './components/checkout-cartLine-delivery-options-radio';
import { GetLineItems } from './components/get-line-items';
import { IImageSettings, RichTextComponent } from '@msdyn365-commerce/core';
import { getShipCarrierAddressAsync } from '../../actions/get-ship-carrier-address.action';
import { ICheckoutDeliveryDateComponentProps } from './components/dlvry-checkout-v2-delivery-date';
import { IDeliveryDateData } from './actions/delivery-date-data';
import { getDeliveryDatesAsync } from './actions/delivery-date-action';
import { CheckoutAppliedStateInput, ICheckoutAppliedState } from '../dlvry-checkout-v2/actions/checkout-applied-state';
import { IModuleStatePropsExt } from '../../shared-utils/types';

export interface ICheckoutDeliveryOptionsModuleProps
    extends IDlvryCheckoutV2DeliveryOptionsProps<IDlvryCheckoutV2DeliveryOptionsData>,
        IModuleStatePropsExt {}

export interface ICheckoutDeliveryOption {
    DeliveryOption: INodeProps;
    description?: React.ReactNode;
    icon?: React.ReactNode;
    subDescription?: React.ReactNode;
    price?: React.ReactNode;
}

export interface ICheckoutDeliveryOptionEdit extends ICheckoutDeliveryOption {
    code: string;
    deliveryOptionWrapper: INodeProps;
    radioButton?: React.ReactNode;
}

export interface ICheckoutDeliveryOptionsList {
    DeliveryOptionsList: INodeProps;
    list: ICheckoutDeliveryOptionEdit[];
}

export interface ICheckoutDeliveryOptionsError {
    Error: INodeProps;
    title: React.ReactNode;
    message: React.ReactNode;
}

export interface ICheckoutDeliveryOptionsViewState {
    isLoading: boolean;
    isError: boolean;
    isShowList: boolean;
    isShowSelected: boolean;
    isShowSaveButton: boolean;
    isShowEditButton: boolean;
    isShowCancelButton: boolean;
}

export interface ICheckoutDeliveryOptionsViewProps extends IDlvryCheckoutV2DeliveryOptionsProps<{}> {
    subHeader: React.ReactNode;
    CheckoutDeliveryOptions: IModuleProps;
    selectedItemId: string;
    deliveryOptionsData: DeliveryOption[];
    isLoading: boolean;
    errorMessageData: string;
    viewState: ICheckoutDeliveryOptionsViewState;
    deliveryOptions?: ICheckoutDeliveryOptionsList;
    deliveryOptionSelected?: ICheckoutDeliveryOption;
    errorMessage?: ICheckoutDeliveryOptionsError;
    waiting?: React.ReactNode;
    saveButton?: React.ReactNode;
    editButton?: React.ReactNode;
    cancelButton?: React.ReactNode;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;
    checkoutCartLineGroupDeliveryOptions?: React.ReactNode;
    enableCheckoutCartLineDeliveryOptions?: boolean;
    onDeliveryOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onSave(): void;
    onEdit(): void;
    onCancel(): void;
    deliveryDateProps: ICheckoutDeliveryDateComponentProps;
}

/**
 *
 * CheckoutDeliveryOptions component.
 * @extends {React.Component<IDlvryCheckoutV2DeliveryOptionsProps<IDlvryCheckoutV2DeliveryOptionsData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutDeliveryOptions extends React.Component<ICheckoutDeliveryOptionsModuleProps> {
    @observable private selectedItemId: string = '';

    @observable private deliveryOptions: DeliveryOption[] = [];

    @observable private isLoading: boolean = false;

    @observable private retrievingDeliveryDates: boolean = true;

    @observable private errorMessageTitle?: string;

    @observable private errorMessage?: string;

    @observable private groupDeliveryOptions: DeliveryOptionsCartLineAddress[] = [];

    @observable private deliveryData: IDeliveryDateData = {};

    @observable private selectedDate?: Date;

    @computed private get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get currencyCode(): string | undefined {
        return get(this.props, 'context.request.channel.Currency');
    }

    public get expressPaymentDetailsFromCartPage() {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
        const properties =
            this.props.data.cart?.result?.cart?.ExtensionProperties?.find(property => property.Key === 'expressPaymentDetails')?.Value
                ?.StringValue ?? '';

        return properties ? JSON.parse(properties) : null;
    }

    public get isExpressPaymentAppliedInCartPage(): boolean {
        return this.expressPaymentDetailsFromCartPage?.isExpressCheckoutAppliedInCartPage ?? false;
    }

    @computed get cartDeliveryMode(): string | undefined {
        return this.getSavedDeliveryOption();
    }

    private retailMultiplePickUpOptionEnabled?: boolean = false;

    private channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    private cachedDeliveryData: {
        [key: string]: IDeliveryDateData;
    } = {};

    private readonly telemetryContent?: ITelemetryContent;

    private readonly isSelectDefaultOptionEnabled?: boolean;

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    private readonly isMultipleAddressShippingEnabled?: boolean;

    private readonly timeoutRetailCall: number;

    public constructor(props: ICheckoutDeliveryOptionsModuleProps) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.isSelectDefaultOptionEnabled =
            this.props.config.useDefaultDeliveryOption && this.props.config.useDefaultDeliveryOption !== useDefaultDeliveryOption.none;
        this.isMultipleAddressShippingEnabled = this.props.config.multipleAddressShippingEnabled;
        this.timeoutRetailCall = this.props.config.timeoutToCallRetail !== undefined ? this.props.config.timeoutToCallRetail : 0;
    }

    public async componentDidMount(): Promise<void> {
        when(
            () => this.isDataReady,
            async () => {
                if (
                    (this.props.data.checkout.result?.isExpressCheckoutApplied && this.canShip()) ||
                    this.isExpressPaymentAppliedInCartPage
                ) {
                    this.selectedDate = this.props.data.checkout.result?.checkoutCart.cart.RequestedDeliveryDate;
                    await this.init();
                } else {
                    // Wait for a specific time (e.g., 2000 milliseconds = 2 seconds)
                    await new Promise(resolve => setTimeout(resolve, this.timeoutRetailCall));
                    // Then call the init function
                    this.selectedDate = this.props.data.checkout.result?.checkoutCart.cart.RequestedDeliveryDate;
                    await this.init();
                }
            }
        );

        when(
            () => this.selectedItemId !== '',
            async () => {
                await this.getDeliveryDates();
            }
        );

        const onShippingAddressChange = async () => {
            await this.loadDeliveryOptions();
            await this.getDeliveryDates();
            this.props.moduleState.setIsCancellable(false);

            await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
        };

        // Wait for shipping address AND delivery options to be loaded before updating delivery options, then listen for changes to shipping address
        void when (
            () => !!this.props.data.checkout.result?.shippingAddress, // && !!this.props.data.deliveryOptions.result,
            async () => {
                await onShippingAddressChange();
                reaction(
                    () => this.props.data.checkout.result?.shippingAddress,
                    onShippingAddressChange
                );
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                if (isExpressCheckoutApplied && this.canShip() && !this.isExpressPaymentAppliedInCartPage) {
                    await this.loadDeliveryOptions();
                    this.props.moduleState.setIsCancellable(false);

                    await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
                }
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (
                        checkoutError &&
                        checkoutError.errorLocation === ErrorLocation.CheckoutDeliveryOptions &&
                        checkoutError.errorMessage
                    ) {
                        this.setError(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutDeliveryOptions) {
                        void focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutDeliveryOptionsModuleProps, nextState: ICheckoutDeliveryOptionsViewState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        this.channelDeliveryOptionConfig = this.props.data.channelDeliveryOptionConfig?.result;
        this.retailMultiplePickUpOptionEnabled = this.props.data.featureState?.result?.find(
            feature => feature.Name === multiplePickupStoreSwitchName
        )?.IsEnabled;

        if (!this.canShip()) {
            this.props.context.telemetry.error('Checkout delivery options content is empty, module wont render');
            return null;
        }

        const {
            moduleState: { isReady, isUpdating, hasError }
        } = this.props;
        const isError = !this.isLoading && hasError;
        const isShowList = this.retrievingDeliveryDates || !this.isLoading && isUpdating && !this.isSelectDefaultOptionEnabled;
        const isShowSelected = !this.isLoading && !hasError && isReady;
        const { errorMessageTitle, deliveryOptionAriaLabel } = this.props.resources;

        const viewProps = {
            ...this.props,
            subHeader: this.props.config.subHeader
                ? <RichTextComponent className='ms-checkout-delivery-options__subheader' text={this.props.config.subHeader} />
                : undefined,
            onDeliveryOptionChange: this.onDeliveryOptionChange,
            selectedItemId: this.selectedItemId,
            deliveryOptionsData: this.deliveryOptions,
            isLoading: this.isLoading,
            alert: this.errorMessage && (
                <div className='ms-checkout-delivery-options__error' role='alert' aria-live='assertive'>
                    <div>
                        <p className='ms-checkout-delivery-options__error-title'>{this.errorMessageTitle || errorMessageTitle}</p>
                        <p className='ms-checkout-delivery-options__error-message'>{this.errorMessage}</p>
                    </div>
                </div>
            ),
            errorMessageData: this.errorMessage,
            viewState: {
                isLoading: this.isLoading,
                isError,
                isShowList,
                isShowSelected,
                isShowSaveButton: isShowList && !this.props.moduleState.hasExternalSubmitGroup,
                isShowEditButton: isShowSelected && !this.props.moduleState.hasExternalSubmitGroup,
                isShowCancelButton: isShowList && !this.props.moduleState.hasExternalSubmitGroup && !!this.getSavedDeliveryOption()
            },
            CheckoutDeliveryOptions: {
                moduleProps: this.props,
                className: classname('ms-checkout-delivery-options', this.props.config.className)
            },
            deliveryOptions: {
                DeliveryOptionsList: {
                    className: 'ms-checkout-delivery-options__list',
                    role: 'list',
                    'aria-label': deliveryOptionAriaLabel
                },
                list: this.getDeliveryOptionList()
            },
            deliveryOptionSelected: this.getDeliveryOptionSelected(),
            errorMessage: this.getErrorMessage(),
            waiting: <Waiting />,
            checkoutErrorRef: this.checkoutErrorRef,
            checkoutCartLineGroupDeliveryOptions: this.getCheckoutCartLineGroupDeliveryOptions(),
            enableCheckoutCartLineDeliveryOptions: this.props.config.multipleAddressShippingEnabled,
            saveButton: this.getSaveButton(),
            editButton: this.getEditButton(),
            cancelButton: this.getCancelButton(),
            onSave: this.onSave,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            deliveryDateProps: {
                leadText: undefined, // this.props.resources.deliveryDateLabel,
                selectedDate: this.hasDeliveryDatesAvailable() ? this.selectedDate : undefined,
                filterDates: this.isSelectableDate,
                onDateChanged: this.onSelectDeliveryDate,
                enabled: !this.retrievingDeliveryDates && isShowList && !isShowSelected && this.hasDeliveryDatesAvailable(),
                working: this.retrievingDeliveryDates,
                hasDeliveryDates: this.hasDeliveryDatesAvailable(),
                resources: this.props.resources
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    @action
    private readonly init = async (): Promise<void> => {
        this.props.moduleState.init({
            onEdit: this.isSelectDefaultOptionEnabled ? undefined : this.onEdit,
            onCancel: this.isSelectDefaultOptionEnabled ? undefined : this.onCancel,
            onSubmit: this.isSelectDefaultOptionEnabled ? undefined : this.onSubmit,
            status:
                !(this.props.config.useDefaultDeliveryOption === useDefaultDeliveryOption.notDisplaySelectedDeliveryOption) &&
                this.canShip()
                    ? 'updating'
                    : 'disabled',
            wide: true
        });

        await this.loadDeliveryOptions();
    };

    private readonly getDeliveryDates = async (): Promise<void> => {
        const localDateTime = new Date();
        const offset = localDateTime.getTimezoneOffset();
        localDateTime.setHours(localDateTime.getHours() - offset / 60);

        // Create hash key for shipping address
        const shippingAddress = (await this.props.data.checkoutAppliedState).newCartObject.ShippingAddress;
        const shippingAddressHash = Md5.hashStr(stringify(shippingAddress) || 'no-address');
        // Check if we have results cached
        const cacheKey = `${shippingAddressHash}-${this.roundDownToNearest30Minutes(localDateTime)}-${this.selectedItemId}`;

        if (this.cachedDeliveryData[cacheKey]) {
            this.deliveryData = this.cachedDeliveryData[cacheKey];
        } else {
            this.retrievingDeliveryDates = true;
            // Use 'setHasError' to disable save and continue button
            this.props.moduleState.setHasError(true);

            const checkoutAppliedState = await this.props.data.checkoutAppliedState;
            const checkout = await this.props.data.checkout;

            const cartLines = checkoutAppliedState.newCartLinesObject && checkoutAppliedState.newCartLinesObject.length > 0
                ? checkoutAppliedState.newCartLinesObject
                : checkout.checkoutCart.cart.CartLines?.map(x => ({
                        ...x,
                        DeliveryMode: this.selectedItemId,
                        ShippingAddress: checkoutAppliedState.newCartObject.ShippingAddress
                    }));

            const cartWithChanges: Cart = {
                ...checkout.checkoutCart.cart,
                ...checkoutAppliedState.newCartObject,
                CartLines: cartLines,
                DeliveryMode: this.selectedItemId
            };

            try {
                this.deliveryData = await getDeliveryDatesAsync(
                    { callerContext: this.props.context.actionContext },
                    checkout.shippingAddress?.LogisticsLocationRecordId || 0,
                    cartWithChanges,
                    localDateTime,
                    this.selectedItemId
                );
                this.cachedDeliveryData[cacheKey] = this.deliveryData;
            } catch (error) {
                console.error(error);
                if ((error as unknown as any).message) {
                    this.setError((error as unknown as Error).message);
                } else {
                    this.setError(this.props.resources.genericErrorMessage);
                }
                this.retrievingDeliveryDates = false;
                return;
            }
        }

        if (!this.deliveryData.DeliveryDates || this.deliveryData.DeliveryDates.length === 0) {
            this.setError('Din leveringskalender er ikke satt opp ennå. Ta kontakt med kundeservice for å fullføre bestillingen.', 'Leveringskalender mangler');
            this.retrievingDeliveryDates = false;
            this.selectedDate = undefined;
            return;
        }

        if (!this.selectedDate || !this.isSelectableDate(this.selectedDate)) {
            const defaultDate = new Date();
            // Add 2 days to the default date
            defaultDate.setDate(defaultDate.getDate() + 2);

            this.selectedDate = this.deliveryData.DeliveryDates?.[0] || defaultDate;
        }

        this.retrievingDeliveryDates = false;
        // Re-enable save and continue button
        this.props.moduleState.setHasError(false);
    };

    private readonly onSelectDeliveryDate = (date: Date) => {
        this.selectedDate = date;
    };

    private readonly hasDeliveryDatesAvailable = (): boolean => {
        const deliveryDates = this.deliveryData.DeliveryDates;

        if (!deliveryDates || deliveryDates.length === 0) {
            return false;
        }

        return deliveryDates.some(date => this.isSelectableDate(date));
    };

    private readonly isSelectableDate = (date: Date): boolean => {
        const deliveryDateData = this.deliveryData;
        const count = deliveryDateData?.DeliveryDates?.length || 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (count === 0) {
            // Don't allow to select dates in the past or today
            if (date < today || (
                date.getDate() === today.getDate() &&
                date.getMonth() === today.getMonth() &&
                date.getFullYear() === today.getFullYear()
            )) {
                return false;
            }

            return true;
        }

        for (let i = 0; i < count; i++) {
            if (
                deliveryDateData?.DeliveryDates &&
                deliveryDateData?.DeliveryDates[i].getDate() === date.getDate() &&
                deliveryDateData?.DeliveryDates[i].getMonth() === date.getMonth() &&
                deliveryDateData?.DeliveryDates[i].getFullYear() === date.getFullYear()
            ) {
                return true;
            }
        }
        return false;
    };

    private readonly roundDownToNearest30Minutes = (date: Date): string => {
        const roundedDate = new Date(date);
        const minutes = roundedDate.getMinutes();
        const remainder = minutes % 30;

        if (remainder !== 0) {
            roundedDate.setMinutes(minutes - remainder);
        }

        roundedDate.setSeconds(0);
        roundedDate.setMilliseconds(0);

        const hours = String(roundedDate.getHours()).padStart(2, '0');
        const roundedMinutes = String(roundedDate.getMinutes()).padStart(2, '0');

        return `${hours}:${roundedMinutes}`;
    };

    private readonly canShip = (): boolean => {
        const hasInvoice = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        return !hasInvoice && this.getCartLinesForShipping().length > 0;
    };

    private readonly isDeliveryModeExists = (deliveryMode: string | undefined): boolean => {
        if (deliveryMode) {
            if (this.deliveryOptions.find(deliveryOption => deliveryOption.Code === deliveryMode)) {
                return true;
            }
        }
        return false;
    };

    private readonly getDeliveryOptionIcon = (deliveryOption: DeliveryOption): JSX.Element => {
        const pickupDeliveryMode = this.props.data.pickupDlvMode.result?.Value;
        const isPickup = !!pickupDeliveryMode && deliveryOption.Code === pickupDeliveryMode;

        return isPickup ? (
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M5 8h10l1 10H4z'></path>
                <path d='M8 8V6a2 2 0 0 1 4 0v2'></path>
                <path d='M3 18h3l2-2h6.5a1.5 1.5 0 0 1 0 3H10'></path>
                <path d='M3 18v-2'></path>
            </svg>
        ) : (
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M3 6h11v10H3z'></path>
                <path d='M14 9h4l3 3v4h-7z'></path>
                <circle cx='7' cy='18' r='1.6'></circle>
                <circle cx='17' cy='18' r='1.6'></circle>
            </svg>
        );
    };

    private readonly getDeliveryOptionSelected = (): ICheckoutDeliveryOption | null => {
        const selectedItem = this.deliveryOptions.find(deliveryOption => deliveryOption.Code === this.selectedItemId);
        if (!selectedItem) {
            this.props.context.telemetry.error('Checkout delivery options content is empty, module wont render');
            return null;
        }

        const { id, typeName, context } = this.props;
        const price = get(selectedItem, 'ShippingChargeAmount', 0);
        const channelConfig = this.props.context.request.channel;
        const subDescription = undefined; // Not implemented yet

        return {
            DeliveryOption: { className: 'ms-checkout-delivery-options__option-selected' },
            description: <DescriptionComponent {...{ description: selectedItem.Description }} />,
            icon: this.getDeliveryOptionIcon(selectedItem),
            subDescription: subDescription ? (
                <div className='ms-checkout-delivery-options__sub-description'>
                    {subDescription}
                </div>
            ) : null,
            price: price === 0 ? (
                <></>
            ) : (
                <PriceComponent
                    id={id}
                    typeName={typeName}
                    context={context}
                    className='ms-checkout-delivery-options__price'
                    data={{ price: { CustomerContextualPrice: price } }}
                    currencyCode={channelConfig?.Currency}
                />
            )
        };
    };

    private readonly getErrorMessage = (): ICheckoutDeliveryOptionsError => {
        const { errorMessageTitle } = this.props.resources;
        return {
            // @ts-expect-error
            CheckoutDeliveryOptionsError: { tag: 'span', className: 'ms-address-form__error', role: 'alert', 'aria-live': 'assertive' },
            title: <ErrorTitleComponent {...{ title: errorMessageTitle }} />,
            message: <ErrorMessageComponent {...{ message: this.errorMessage }} />
        };
    };

    private readonly getDeliveryOptionList = (): ICheckoutDeliveryOptionEdit[] => {
        const count = this.deliveryOptions.length;
        const { priceFree } = this.props.resources;
        const { id, typeName, context } = this.props;
        const channelConfig = this.props.context.request.channel;

        return this.deliveryOptions.map((deliveryOption: DeliveryOption, index: number) => {
            const getAriaLabelWithPrice = get(deliveryOption, 'ShippingChargeAmount', 0);
            const subDescription = undefined; // Not implemented yet
            return {
                DeliveryOption: { className: 'ms-checkout-delivery-options__option', role: 'listitem' },
                code: deliveryOption.Code || '',
                deliveryOptionWrapper: { className: 'ms-checkout-delivery-options__option-wrapper', tag: 'label' },
                radioButton: (
                    <RadioButtonComponent
                        {...{
                            disabled: this.retrievingDeliveryDates,
                            isChecked: deliveryOption.Code === this.selectedItemId,
                            value: deliveryOption.Code,
                            ariaSetSize: count,
                            ariaPosInSet: index + 1,
                            ariaLabel: `${deliveryOption.Description!} ${getAriaLabelWithPrice === 0 ? priceFree : getAriaLabelWithPrice}`,
                            onChange: this.onDeliveryOptionChange,
                            telemetryContent: this.telemetryContent
                        }}
                    />
                ),
                description: (
                    <DescriptionComponent
                        {...{
                            description: deliveryOption.Description
                        }}
                    />
                ),
                icon: this.getDeliveryOptionIcon(deliveryOption),
                subDescription: subDescription ? (
                    <div className='ms-checkout-delivery-options__sub-description'>
                        {subDescription}
                    </div>
                ) : null,
                price: get(deliveryOption, 'ShippingChargeAmount', 0) === 0 ? (
                    <></>
                ) : (
                    <PriceComponent
                        id={id}
                        typeName={typeName}
                        context={context}
                        className='ms-checkout-delivery-options__price'
                        data={{ price: { CustomerContextualPrice: get(deliveryOption, 'ShippingChargeAmount', 0) } }}
                        currencyCode={channelConfig?.Currency}
                    />
                )
            };
        });
    };

    private readonly getSaveButton = (): React.ReactNode => {
        const { saveBtnLabel = 'Save' } = this.props.resources;
        return (
            <Button
                className='ms-checkout-delivery-options__btn-save'
                title={saveBtnLabel}
                color='primary'
                disabled={!this.selectedItemId}
                onClick={this.onSave}
            >
                {saveBtnLabel}
            </Button>
        );
    };

    private readonly getEditButton = (): React.ReactNode => {
        const { editBtnLabel = 'Change' } = this.props.resources;
        return (
            <Button className='ms-checkout-delivery-options__btn-edit' title={editBtnLabel} color='primary' onClick={this.onEdit}>
                {editBtnLabel}
            </Button>
        );
    };

    private readonly getCancelButton = (): React.ReactNode => {
        const { cancelBtnLabel = 'Cancel' } = this.props.resources;
        return (
            <Button className='ms-checkout-delivery-options__btn-cancel' title={cancelBtnLabel} color='secondary' onClick={this.onCancel}>
                {cancelBtnLabel}
            </Button>
        );
    };

    private readonly setError = (errorMessage: string, errorTitle?: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.errorMessage = errorMessage;
        this.errorMessageTitle = errorTitle;
    };

    private readonly clearError = (): void => {
        this.props.moduleState.setHasError(false);
        this.errorMessage = undefined;
        this.errorMessageTitle = undefined;
    };

    private readonly onSave = async (): Promise<void> => {
        await this.onSubmit();
    };

    private readonly onEdit = async (): Promise<void> => {
        this.props.telemetry.information('Delivery options onEdit is called.');

        this.clearError();
        this.onUpdating();
        await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Delivery options onSubmit is called.');

        this.props.moduleState.onPending();

        const appliedState: ICheckoutAppliedState = { ...await this.props.data.checkoutAppliedState };
        appliedState.applied.deliveryOptions = false;

        try {
            // const cartState = (await this.props.data.checkout).checkoutCart;

            await this.updateCartDeliverySpecification(this.selectedItemId, false);
            //await cartState.updateCartLines({ updatedCartLinesObject: this.props.data.checkoutAppliedState.result!.newCartLinesObject });

            await this.saveRequestedDeliveryDate();
            //await cartState.updateCart({ newCartObject: this.props.data.checkoutAppliedState.result!.newCartObject as Cart });

            await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });

            appliedState.applied.deliveryOptions = true;

            this.onReady();
            this.props.moduleState.setIsCancellable(true);
        } catch (error) {
            console.error(error);
            this.setError(this.props.resources.genericErrorMessage);
        }
    };

    private readonly onCancel = async (): Promise<void> => {
        this.props.telemetry.information('Delivery options onCancel is called.');

        // Reset to the saved value
        const savedDeliveryOption = this.getSavedDeliveryOption();
        if (savedDeliveryOption) {
            this.selectedItemId = savedDeliveryOption;
            this.onReady();

            await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
        } else {
            this.onUpdating();
        }
    };

    private readonly getSavedDeliveryOption = (): string | undefined => {
        const cartLinesForShipping = this.getCartLinesForShipping();
        return cartLinesForShipping[0] && cartLinesForShipping[0].DeliveryMode;
    };

    private readonly updateCartDeliverySpecification = async (deliveryModeId: string, setReady?: boolean): Promise<void> => {
        if (deliveryModeId.trim() === '') {
            return;
        }

        /* const { resources } = this.props;
        const checkoutState = this.props.data.checkout.result as ICheckoutState;
        const updateCartDeliverySpecificationInput = {
            deliveryModeId,
            shippingAddress: checkoutState.shippingAddress,
            channelDeliveryOptionConfig: this.retailMultiplePickUpOptionEnabled ? this.channelDeliveryOptionConfig : undefined
        }; */

        this.clearError();
        this.onPending();

        const newApplied: ICheckoutAppliedState = { ...await this.props.data.checkoutAppliedState };
        newApplied.newCartObject = {
            ...newApplied.newCartObject,
            DeliveryMode: deliveryModeId
        };
        newApplied.newCartLinesObject = this.props.data.checkout.result!.checkoutCart.cart.CartLines!.map(x => {
            return {
                ...x,
                DeliveryMode: deliveryModeId,
                ShippingAddress: newApplied.newCartObject.ShippingAddress
            };
        });
        this.props.context.actionContext.update(new CheckoutAppliedStateInput(), newApplied);

        if (setReady === true || setReady === undefined) {
            this.onReady();
            this.props.moduleState.setIsCancellable(true);
        }

        /* await checkoutState.checkoutCart
            .updateCartDeliverySpecification(updateCartDeliverySpecificationInput)
            .then(response => {
                if (response.status === 'FAILED') {
                    const errorMessage = `${response.errorDetails?.LocalizedMessage}`;
                    this.setError(StringExtensions.isNullOrEmpty(errorMessage) ? resources.genericErrorMessage : errorMessage);
                }

                this.onReady();
                this.props.moduleState.setIsCancellable(true);
            })
            .catch(() => {
                this.setError(resources.genericErrorMessage);
            }); */
    };

    private readonly saveRequestedDeliveryDate = async (): Promise<void> => {
        const cartState = (await this.props.data.checkout).checkoutCart;
        const cart = cartState?.cart;

        if (!cartState || !cart) {
            return;
        }

        const deliveryDate = this.selectedDate;
        deliveryDate?.setUTCHours(0, 0, 0, 0);
        const newApplied: ICheckoutAppliedState = { ...await this.props.data.checkoutAppliedState };
        newApplied.newCartObject = {
            ...newApplied.newCartObject,
            RequestedDeliveryDate: deliveryDate
        };
        newApplied.newCartLinesObject.forEach(x => x.RequestedDeliveryDate = deliveryDate);
        this.props.context.actionContext.update(new CheckoutAppliedStateInput(), newApplied);

        /* if (cartState && cart) {
            // @ts-ignore
            cart.RequestedDeliveryDate = this.selectedDate;

            const cartLines: CartLine[] = cart.CartLines!;
            if (cartLines && cartLines.length > 0) {
                cartLines.forEach(line => line.RequestedDeliveryDate = this.selectedDate);
            }
            const updateCartInput = {
                newCartObject: cart
            };
            const updateCartLinesInput = {
                updatedCartLinesObject: cartLines
            };
            await cartState.updateCart(updateCartInput);
            await cartState.updateCartLines(updateCartLinesInput);
        } */
    };

    private readonly getCartLinesForShipping = (): CartLine[] => {
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;

        return this.retailMultiplePickUpOptionEnabled
            ? ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !==
                            this.channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(
                                deliveryMode => deliveryMode === cartLine.DeliveryMode
                            ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              )
            : ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              );
    };

    private readonly getDeliveryOptions = async (shippingAddress: Address): Promise<void> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const {
            context,
            resources
        } = this.props;
        // const deliveryOptions = await this.props.data.deliveryOptions;
        const shipCarrierAddress = await getShipCarrierAddressAsync(context.actionContext, shippingAddress.LogisticsLocationRecordId || 0);

        if (context) {
            this.isLoading = true;
            this.deliveryOptions = [];
            this.selectedItemId = '';
            this.clearError();

            try {
                // Retrieve delivery option from organization information if any
                const orgDelivery = this.props.data.organizationInformation?.result?.ExtensionProperties?.find(
                    property => property.Key === 'DLVMODE'
                )?.Value?.StringValue;

                // Use delivery option from ship carrier API if available, otherwise use fallback delivery option if available.
                // Finally, use undefined if no delivery option is available.
                const delivery: DeliveryOption | undefined = shipCarrierAddress && shipCarrierAddress.DlvModeId
                    ? {
                        RecordId: shipCarrierAddress.RecId!,
                        Code: shipCarrierAddress.DlvModeId,
                        Description: resources.deliveryDeliveryOptionDescriptionLabel || 'Levering'
                        /* ShippingChargeAmount: deliveryOptions[0]?.DeliveryOptions
                            ?.find(x => x.Code === shipCarrierAddress.DlvModeId)?.ShippingChargeAmount */
                    }
                    : {
                        RecordId: 0,
                        Code: orgDelivery || 'Levering', // Hardcoded value is last resort
                        Description: resources.deliveryDeliveryOptionDescriptionLabel || 'Levering'
                        /* ShippingChargeAmount: deliveryOptions[0]?.DeliveryOptions
                            ?.find(x => x.Code === (orgDelivery || 'Levering'))?.ShippingChargeAmount */
                    };

                const pickup: DeliveryOption | undefined = this.props.data.pickupDlvMode.result
                    ? {
                          RecordId: this.props.data.pickupDlvMode.result.RecId!,
                          Code: this.props.data.pickupDlvMode.result.Value,
                          Description: this.props.resources.pickupDeliveryOptionDescriptionLabel || 'Henting'
                      }
                    : undefined;

                this.deliveryOptions = [delivery, pickup!].filter(Boolean);
            } catch {
                this.setError(resources.genericErrorMessage);
            }

            if (this.deliveryOptions.length === 0) {
                this.setError(resources.deliveryOptionsNotFound);
            } else if (this.cartDeliveryMode && this.isDeliveryModeExists(this.cartDeliveryMode)) {
                this.selectedItemId = this.cartDeliveryMode;

                await this.saveRequestedDeliveryDate();
                await this.updateCartDeliverySpecification(this.selectedItemId, false);

                // Set state to ready but don't submit. onEdit works best for this
                await this.onEdit();

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
                this.props.data.checkout.result?.updateHasCartDeliveryMode({ newHasCartDeliveryMode: true });

                if (!this.props.data.checkout.result?.isPaymentVerificationRedirection) {
                    await this.props.data.checkout.result.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
                }
            } else {
                this.onUpdating();
                this.selectedItemId = this.deliveryOptions.length > 0 ? this.deliveryOptions[0].Code || '' : ''; // By default, select first delivery

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
                this.props.data.checkout.result?.updateHasCartDeliveryMode({ newHasCartDeliveryMode: !!this.isSelectDefaultOptionEnabled });
            }

            this.isLoading = false;
        }
    };

    private readonly getStoredDeliveryOptions = async (): Promise<void> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const { context } = this.props;
        const cartState = this.props.data.checkout.result.checkoutCart;

        if (context) {
            this.isLoading = true;
            this.deliveryOptions = [];
            this.selectedItemId = '';
            this.clearError();

            let originalDeliveryMode: string = '';
            const shippingCartLines = this.getCartLinesForShipping();
            const hasShippingDeliveryLines = shippingCartLines && shippingCartLines.length > 0 && shippingCartLines[0].DeliveryMode;
            if (hasShippingDeliveryLines) {
                // First store the delivery mode, so that we can restore the shipping information.
                originalDeliveryMode = shippingCartLines[0].DeliveryMode || '';

                // Clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                const cart = cartState.cart;
                await cartState.clearCartLinesDeliveryInformation({
                    cart,
                    channelDeliveryOptionConfiguration: this.channelDeliveryOptionConfig
                });
                if (hasShippingDeliveryLines) {
                    await this.updateCartDeliverySpecification(originalDeliveryMode);
                }
            }

            if (this.cartDeliveryMode) {
                this.selectedItemId = this.cartDeliveryMode;

                await this.saveRequestedDeliveryDate();
                await this.updateCartDeliverySpecification(this.selectedItemId, false);

                this.onReady();

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
                this.props.data.checkout.result?.updateHasCartDeliveryMode({ newHasCartDeliveryMode: true });

                if (!this.props.data.checkout.result.isPaymentVerificationRedirection) {
                    await this.props.data.checkout.result.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
                }
            }

            this.isLoading = false;
        }
    };

    private readonly loadDeliveryOptions = async (): Promise<void> => {
        const shippingAddress = this.props.data.checkout.result?.shippingAddress;
        const cartLinesForShipping = this.props.data.checkout.result?.cartLinesWithShippingAddress;
        const requestContext = this.props.context.actionContext.requestContext;
        if (requestContext.features && requestContext.features.enable_checkout_race_condition_hot_fix) {
            if (this.canShip() && (shippingAddress || cartLinesForShipping)) {
                if (this.props.data.checkout.result?.isPaymentVerificationRedirection) {
                    await this.getStoredDeliveryOptions();
                } else {
                    if (
                        !this.props.data.checkout.result?.isPaymentVerificationRedirection ||
                        this.isSelectDefaultOptionEnabled ||
                        this.props.data.checkout.result?.isExpressCheckoutApplied
                    ) {
                        this.isMultipleAddressShippingEnabled
                            ? await this.getGroupDeliveryOptions()
                            : shippingAddress !== undefined
                            ? await this.getDeliveryOptions(shippingAddress)
                            : null;
                    }

                    if (
                        this.isSelectDefaultOptionEnabled ||
                        this.props.data.checkout.result?.isExpressCheckoutApplied ||
                        this.isExpressPaymentAppliedInCartPage
                    ) {
                        void this.onSubmit();
                    }
                }
            }
        } else {
            if (this.canShip() && (shippingAddress || cartLinesForShipping)) {
                if (
                    !this.props.data.checkout.result?.isPaymentVerificationRedirection ||
                    this.isSelectDefaultOptionEnabled ||
                    this.props.data.checkout.result?.isExpressCheckoutApplied
                ) {
                    this.isMultipleAddressShippingEnabled
                        ? await this.getGroupDeliveryOptions()
                        : shippingAddress !== undefined
                        ? await this.getDeliveryOptions(shippingAddress)
                        : null;
                }
                if (this.props.data.checkout.result?.isPaymentVerificationRedirection) {
                    await this.getStoredDeliveryOptions();
                }

                if (
                    this.isSelectDefaultOptionEnabled ||
                    this.props.data.checkout.result?.isExpressCheckoutApplied ||
                    this.isExpressPaymentAppliedInCartPage
                ) {
                    void this.onSubmit();
                }
            }
        }
    };

    private readonly onDeliveryOptionChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const deliveryModeId = event.currentTarget.value;
        this.selectedItemId = deliveryModeId;
        await this.getDeliveryDates();
    };

    private readonly onReady = () => {
        if (!(this.props.config.useDefaultDeliveryOption === useDefaultDeliveryOption.notDisplaySelectedDeliveryOption)) {
            this.props.moduleState.onReady();
        }
    };

    private readonly onUpdating = () => {
        if (!(this.props.config.useDefaultDeliveryOption === useDefaultDeliveryOption.notDisplaySelectedDeliveryOption)) {
            this.props.moduleState.onUpdating();
        }
    };

    private readonly onPending = () => {
        if (!this.isSelectDefaultOptionEnabled) {
            this.props.moduleState.onPending();
        }
    };

    private readonly getCheckoutCartLineGroupDeliveryOptions = (): React.ReactNode => {
        const { resources, config } = this.props;
        const shippingCartLines = this.getCartLinesForShipping();
        if (!ArrayExtensions.hasElements(shippingCartLines) || !ArrayExtensions.hasElements(this.groupDeliveryOptions)) {
            return null;
        }
        const imageSettings = this.getImageSettings(config.imageSettings);
        return (
            <div className='ms-checkout-delivery-options__cart-line-grp'>
                {this.groupDeliveryOptions.map((groupDeliveryOption: DeliveryOptionsCartLineAddress, index) => {
                    const cartLine = shippingCartLines.filter(
                        cartLine => groupDeliveryOption && (groupDeliveryOption.cartLineIds?.[0] === cartLine?.LineId?.toString() ?? '')
                    );
                    return (
                        <div className='ms-checkout-delivery-options__cart-line-grp__item' key={index}>
                            <div className='ms-checkout-delivery-options__cart-line-grp__item__header'>
                                <AddressDetailItemFunctionComponent
                                    isNewLine={true}
                                    isShowLabel={true}
                                    labelText={resources.cartLineGroupShippingAddressLabel}
                                    name='Address'
                                    value={cartLine[0].ShippingAddress?.FullAddress ?? ''}
                                />
                            </div>
                            <div className='ms-checkout-delivery-options__cart-line-grp__item__ctn'>
                                <div className='ms-checkout-delivery-options__cart-line-grp__item__ctn__lbl'>
                                    <span className='ms-checkout-delivery-options__cart-line-grp__item__ctn__lbl_h'>
                                        {resources.cartLineGroupDeliveryOptionsLabel}
                                    </span>
                                    <span className='ms-checkout-delivery-options__cart-line-grp__item__ctn__lbl_t'>
                                        {resources.cartLineGroupDeliveryOptionsText}
                                    </span>
                                </div>
                                <CheckoutCartLineDeliveryDate
                                    ariaLabel={resources.cartLineGroupDeliveryOptionsLabel}
                                    telemetryContent={this.telemetryContent}
                                    disclaimerText={resources.cartLineGroupDeliveryOptionsText}
                                    heading={resources.cartLineGroupDeliveryOptionsLabel}
                                />
                                <CheckoutCartLineDeliveryOptionsRadio
                                    deliveryOptions={groupDeliveryOption.deliveryOptions}
                                    priceFree={resources.priceFree}
                                    selectedItemId={this.selectedItemId}
                                    typeName={this.props.typeName}
                                    context={this.props.context}
                                    onChange={this.onDeliveryOptionChange}
                                    ariaLabel={resources.cartLineGroupDeliveryOptionsLabel}
                                    telemetryContent={this.telemetryContent}
                                    moduleId={''}
                                    ariaSetSize={0}
                                    ariaPosInSet={0}
                                />
                                <GetLineItems
                                    context={this.props.context}
                                    cartLines={cartLine}
                                    products={this.props.data.products?.result}
                                    imageSettings={imageSettings}
                                    gridSettings={this.props.context.request.gridSettings}
                                    resources={{
                                        configString: resources.configString,
                                        discountStringText: resources.discountStringText,
                                        shippingChargesText: resources.shippingChargesString,
                                        productDimensionTypeColor: resources.productDimensionTypeColor,
                                        productDimensionTypeSize: resources.productDimensionTypeSize,
                                        productDimensionTypeStyle: resources.productDimensionTypeStyle,
                                        productDimensionTypeAmount: resources.productDimensionTypeAmount,
                                        quantityDisplayStringLabel: resources.quantityDisplayString
                                    }}
                                    currencyCode={this.currencyCode}
                                    catalogs={this.props.data.catalogs?.result}
                                    moduleId={this.props.id}
                                    typeName={this.props.typeName}
                                    showShippingChargesForLineItems={true} // Add this line
                                    deliveryOptions={groupDeliveryOption.deliveryOptions} // Add this line
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    private readonly getGroupDeliveryOptions = async (): Promise<void> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const { context, resources, config } = this.props;
        const cartState = this.props.data.checkout.result?.checkoutCart;

        if (context) {
            this.isLoading = true;
            this.deliveryOptions = [];
            this.selectedItemId = '';
            this.clearError();

            try {
                let originalDeliveryMode: string = '';
                const shippingCartLines = this.getCartLinesForShipping();
                const cartLinesForShipping: CartLine[] = [];
                if (ArrayExtensions.hasElements(shippingCartLines)) {
                    const getGroupByDelivery = (items: CartLine[]) => groupBy(items, (item: CartLine) => item.ShippingAddress?.RecordId);
                    cartLinesForShipping.push(getGroupByDelivery(shippingCartLines));
                    console.log('running delivery option for all lines', cartLinesForShipping);
                }
                const hasShippingDeliveryLines = shippingCartLines && shippingCartLines.length > 0 && shippingCartLines[0].DeliveryMode;
                if (hasShippingDeliveryLines) {
                    // First store the delivery mode, so that we can restore the shipping information.
                    originalDeliveryMode = shippingCartLines[0].DeliveryMode || '';

                    // Clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                    const cart = cartState.cart;
                    await cartState.clearCartLinesDeliveryInformation({
                        cart,
                        channelDeliveryOptionConfiguration: this.channelDeliveryOptionConfig
                    });
                }

                // TODO: Check if we can move this getDeliveryOptions to checkout state
                let deliveryOptionFilter: FilterDeliveryModeOption;
                switch (config.filterDeliveryModeOption) {
                    case filterDeliveryModeOption.none:
                        deliveryOptionFilter = FilterDeliveryModeOption.None;
                        break;
                    case filterDeliveryModeOption.filterOutNonCarrierDeliveryMode:
                        deliveryOptionFilter = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
                        break;
                    default:
                        deliveryOptionFilter = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
                }
                const inputData = new GetDeliveryOptionsForCartLineAddressInput(deliveryOptionFilter, shippingCartLines);
                const response = await getDeliveryOptionsForCartLineAddress(inputData, context.actionContext);
                if (response && response.length > 0) {
                    this.groupDeliveryOptions = response;
                }

                if (hasShippingDeliveryLines) {
                    await this.updateCartDeliverySpecification(originalDeliveryMode);
                }
            } catch {
                this.setError(resources.genericErrorMessage);
            }
        }
        this.isLoading = false;
    };

    /**
     * Gets the image settings.
     * @param imageSettings - Image settings.
     * @returns IImageSettings.
     */
    private readonly getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
                    sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
                    lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
                },
                lazyload: true
            }
        );
    };
}

export default CheckoutDeliveryOptions;
